"use client"

import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { UserPlus, Mail, Clock, CheckCircle, XCircle, RefreshCw, Eye, Send, Calendar, AlertCircle, MoreVertical } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu.jsx'
import { Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle, SheetDescription } from '../ui/sheet.jsx'
import { VisuallyHidden } from '../ui/visually-hidden.jsx'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL
const API = `${BACKEND_URL}/api`

// Performance optimized Team Invitations component
export function TeamInvitations() {
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    status: '',
    owner: ''
  })
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [selectedInvitation, setSelectedInvitation] = useState(null)
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false)
  const [error, setError] = useState('')
  const [stats, setStats] = useState({ total: 0, pending: 0, delivered: 0, accepted: 0 })
  const [wsConnected, setWsConnected] = useState(false)

  // WebSocket connection for real-time updates
  useEffect(() => {
    const wsUrl = `${BACKEND_URL.replace('http', 'ws')}/ws/user_123` // In real app, use actual user ID
    const ws = new WebSocket(wsUrl)
    
    ws.onopen = () => {
      setWsConnected(true)
      console.log('WebSocket connected for real-time invitation updates')
    }
    
    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)
        if (message.type.startsWith('invitation_')) {
          console.log('Real-time invitation update:', message)
          loadInvitations() // Refresh invitations list
        }
      } catch (e) {
        console.error('WebSocket message parse error:', e)
      }
    }
    
    ws.onclose = () => {
      setWsConnected(false)
      console.log('WebSocket connection closed')
    }
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
    }
    
    // Cleanup
    return () => {
      ws.close()
    }
  }, [])

  // Load invitations with performance optimization
  const loadInvitations = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      
      const params = new URLSearchParams()
      if (filters.status) params.append('status', filters.status)
      if (filters.owner) params.append('owner', filters.owner)
      params.append('limit', '100')

      const { data } = await axios.get(`${API}/admin/team/invitations?${params}`, { withCredentials: true })
      setInvitations(data.items || [])
      
      // Calculate stats
      const total = data.items?.length || 0
      const pending = data.items?.filter(i => i.status === 'pending').length || 0
      const delivered = data.items?.filter(i => i.status === 'delivered').length || 0
      const accepted = data.items?.filter(i => i.status === 'accepted').length || 0
      setStats({ total, pending, delivered, accepted })
      
    } catch (err) {
      setError('Failed to load invitations')
      console.error('Invitations error:', err)
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadInvitations()
  }, [loadInvitations])

  // Get status styling
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100'
      case 'delivered': return 'text-blue-600 bg-blue-100'
      case 'viewed': return 'text-purple-600 bg-purple-100'
      case 'accepted': return 'text-green-600 bg-green-100'
      case 'expired': return 'text-red-600 bg-red-100'
      case 'revoked': return 'text-gray-600 bg-gray-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return Clock
      case 'delivered': return Mail
      case 'viewed': return Eye
      case 'accepted': return CheckCircle
      case 'expired': return XCircle
      case 'revoked': return XCircle
      default: return Clock
    }
  }

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Never'
    const date = new Date(timestamp)
    const now = new Date()
    const diffMinutes = Math.floor((now - date) / (1000 * 60))
    
    if (diffMinutes < 60) return `${diffMinutes}m ago`
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`
    return `${Math.floor(diffMinutes / 1440)}d ago`
  }

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'viewed', label: 'Viewed' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'expired', label: 'Expired' },
    { value: 'revoked', label: 'Revoked' }
  ]

  return (
    <div className="team-invitations mobile-full-width">
      {/* Header with stats - Mobile Responsive */}
      <div className="flex-col-mobile items-start justify-between mb-6 mobile-full-width">
        <div className="w-full mobile-full-width">
          <h2 className="text-2xl font-semibold text-primary mb-2">Team Invitations</h2>
          <div className="flex-col-mobile gap-4 text-sm text-muted mobile-full-width">
            <div className="flex gap-4 flex-wrap mobile-full-width">
              <span><UserPlus size={16} className="inline mr-1" />{stats.total} Total</span>
              <span><Clock size={16} className="inline mr-1 text-yellow-600" />{stats.pending} Pending</span>
              <span><Mail size={16} className="inline mr-1 text-blue-600" />{stats.delivered} Delivered</span>
              <span><CheckCircle size={16} className="inline mr-1 text-green-600" />{stats.accepted} Accepted</span>
            </div>
            {wsConnected && (
              <span className="text-green-600">
                <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-1"></span>Live
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto mobile-full-width">
          <button 
            onClick={loadInvitations} 
            className="btn secondary flex-1 sm:flex-initial"
            disabled={loading}
            aria-label="Refresh invitations list"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
            <span className="hidden sm:inline">Refresh</span>
            <VisuallyHidden>
              {loading ? 'Refreshing invitation data' : 'Reload invitation data from server'}
            </VisuallyHidden>
          </button>
          <button 
            onClick={() => setShowCreateForm(true)}
            className="btn primary flex-1 sm:flex-initial"
            aria-label="Create new team invitation"
          >
            <UserPlus size={16} aria-hidden="true" />
            <span className="hidden sm:inline">Send Invitation</span>
            <VisuallyHidden>
              Open form to send a new team invitation
            </VisuallyHidden>
          </button>
        </div>
      </div>

      {/* Filters - Mobile Responsive */}
      <div className="flex gap-4 mb-6 w-full mobile-full-width">
        <select
          className="input w-full sm:min-w-40 sm:w-auto mobile-full-width"
          value={filters.status}
          onChange={(e) => setFilters({...filters, status: e.target.value})}
          aria-label="Filter invitations by status"
        >
          {statusOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <VisuallyHidden>
          Filter the invitation list by status to focus on pending, delivered, accepted, or other invitation states.
        </VisuallyHidden>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error mb-4" role="alert">
          {error}
        </div>
      )}

      {/* Invitations List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-pulse">Loading invitations...</div>
        </div>
      ) : invitations.length === 0 ? (
        <div className="text-center py-8">
          <UserPlus size={48} className="mx-auto text-muted mb-4" />
          <p className="text-muted">No invitations found</p>
          <button 
            onClick={() => setShowCreateForm(true)}
            className="btn primary mt-4"
          >
            <UserPlus size={16} />
            Send First Invitation
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {invitations.map((invitation) => (
            <InvitationCard
              key={invitation.id}
              invitation={invitation}
              onDetailsClick={() => {
                setSelectedInvitation(invitation)
                setShowDetailsDrawer(true)
              }}
              onRefresh={loadInvitations}
              getStatusColor={getStatusColor}
              getStatusIcon={getStatusIcon}
              formatTimeAgo={formatTimeAgo}
            />
          ))}
        </div>
      )}

      {/* Create Invitation Form */}
      {showCreateForm && (
        <CreateInvitationForm 
          onClose={() => setShowCreateForm(false)}
          onSuccess={loadInvitations}
        />
      )}

      {/* Invitation Details Drawer */}
      {showDetailsDrawer && selectedInvitation && (
        <InvitationDetailsDrawer
          invitation={selectedInvitation}
          onClose={() => setShowDetailsDrawer(false)}
          onRefresh={loadInvitations}
          formatTimeAgo={formatTimeAgo}
          getStatusColor={getStatusColor}
        />
      )}
    </div>
  )
}

// Individual Invitation Card Component
function InvitationCard({ invitation, onDetailsClick, onRefresh, getStatusColor, getStatusIcon, formatTimeAgo }) {
  const [actionLoading, setActionLoading] = useState(false)
  const StatusIcon = getStatusIcon(invitation.status)

  const performAction = async (action, justification = 'Quick action performed') => {
    try {
      setActionLoading(true)
      
      await axios.post(
        `${API}/admin/team/invitations/${invitation.id}/actions`,
        { action, justification },
        { withCredentials: true }
      )
      
      onRefresh()
      
    } catch (err) {
      alert(`Action failed: ${err.response?.data?.detail || err.message}`)
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div className="card p-4 hover:shadow-md transition-shadow mobile-full-width">
      <div className="card-mobile-stack mobile-full-width">
        <div className="flex-1 min-w-0 mobile-full-width">
          <div className="flex items-center gap-3 mb-2 mobile-full-width">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-sm font-medium text-primary">
                {invitation.email.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0 mobile-full-width">
              <h3 className="font-medium text-primary truncate">{invitation.email}</h3>
              <p className="text-sm text-muted truncate">{invitation.role_template_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted mb-3 flex-wrap mobile-full-width">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invitation.status)}`}>
              <StatusIcon size={12} />
              {invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)}
            </span>
            <span className="text-xs">Created {formatTimeAgo(invitation.created_at)}</span>
            {invitation.sent_at && <span className="text-xs">Sent {formatTimeAgo(invitation.sent_at)}</span>}
            {invitation.opened_at && <span className="text-xs">Viewed {formatTimeAgo(invitation.opened_at)}</span>}
          </div>

          {invitation.message && (
            <p className="text-sm text-muted italic mb-2 line-clamp-2">"{invitation.message}"</p>
          )}

          <div className="flex items-center gap-2 text-xs text-muted flex-wrap mobile-full-width">
            {invitation.mfa_required && (
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle size={12} />
                MFA Required
              </span>
            )}
            <span>Exp: {new Date(invitation.expires_at).toLocaleDateString()}</span>
            <span>Resends: {invitation.resend_count || 0}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onDetailsClick}
            className="btn secondary text-xs"
            aria-label={`View details for invitation to ${invitation.email}`}
          >
            Details
            <VisuallyHidden>
              View detailed information about the invitation to {invitation.email}
            </VisuallyHidden>
          </button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="icon-btn" disabled={actionLoading} aria-label={`Actions for invitation to ${invitation.email}`}>
                <MoreVertical size={16} aria-hidden="true" />
                <VisuallyHidden>
                  Open actions menu for invitation to {invitation.email}
                </VisuallyHidden>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {invitation.status === 'pending' && (
                <>
                  <DropdownMenuItem onClick={() => performAction('resend')}>
                    <Send size={14} className="mr-2" aria-hidden="true" />
                    Resend
                    <VisuallyHidden>Resend invitation email to {invitation.email}</VisuallyHidden>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => performAction('extend', 'Extended invitation deadline')}>
                    <Calendar size={14} className="mr-2" aria-hidden="true" />
                    Extend
                    <VisuallyHidden>Extend expiration date for invitation to {invitation.email}</VisuallyHidden>
                  </DropdownMenuItem>
                </>
              )}
              {invitation.status !== 'revoked' && invitation.status !== 'accepted' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => performAction('revoke', 'Invitation revoked by admin')}
                    className="text-red-600"
                  >
                    <XCircle size={14} className="mr-2" aria-hidden="true" />
                    Revoke
                    <VisuallyHidden>Revoke and cancel invitation to {invitation.email}</VisuallyHidden>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  )
}

// Create Invitation Form Component
function CreateInvitationForm({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    email: '',
    role_template: '',
    mfa_required: true,
    expires_in_days: 7,
    message: '',
    justification: ''
  })
  const [roleTemplates, setRoleTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Load role templates
  useEffect(() => {
    const loadRoleTemplates = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/team/role-templates`, { withCredentials: true })
        setRoleTemplates(data)
      } catch (err) {
        console.error('Failed to load role templates:', err)
      }
    }
    loadRoleTemplates()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await axios.post(`${API}/admin/team/invitations`, formData, { withCredentials: true })
      onSuccess()
      onClose()
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send invitation')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-canvas rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold text-primary mb-4">Send Team Invitation</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              Email Address
            </label>
            <input
              type="email"
              className="input w-full"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              Role Template
            </label>
            <select
              className="input w-full"
              value={formData.role_template}
              onChange={(e) => setFormData({...formData, role_template: e.target.value})}
              required
            >
              <option value="">Select a role...</option>
              {roleTemplates.map(template => (
                <option key={template.key} value={template.key}>
                  {template.name}
                </option>
              ))}
            </select>
            {formData.role_template && (
              <p className="text-xs text-muted mt-1">
                {roleTemplates.find(t => t.key === formData.role_template)?.description}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              Expires in (days)
            </label>
            <input
              type="number"
              min="1"
              max="30"
              className="input w-full"
              value={formData.expires_in_days}
              onChange={(e) => setFormData({...formData, expires_in_days: parseInt(e.target.value)})}
            />
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.mfa_required}
                onChange={(e) => setFormData({...formData, mfa_required: e.target.checked})}
              />
              <span className="text-sm text-primary">Require MFA setup</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              Personal Message (Optional)
            </label>
            <textarea
              className="input w-full"
              rows="3"
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              placeholder="Welcome to the team! We're excited to have you join us."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-primary mb-1">
              Justification
            </label>
            <input
              type="text"
              className="input w-full"
              value={formData.justification}
              onChange={(e) => setFormData({...formData, justification: e.target.value})}
              placeholder="Reason for this invitation"
              required
            />
          </div>

          {error && (
            <div className="error" role="alert">
              {error}
            </div>
          )}

          <div className="flex-col-mobile gap-3 pt-4">
            <button
              type="submit"
              className="btn primary w-full"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn secondary w-full sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Invitation Details Drawer Component  
function InvitationDetailsDrawer({ invitation, onClose, onRefresh, formatTimeAgo, getStatusColor }) {
  const [loading, setLoading] = useState(false)
  const [details, setDetails] = useState(invitation)

  // Load detailed invitation data
  useEffect(() => {
    const loadDetails = async () => {
      try {
        setLoading(true)
        const { data } = await axios.get(`${API}/admin/team/invitations/${invitation.id}`, { withCredentials: true })
        setDetails(data)
      } catch (err) {
        console.error('Failed to load invitation details:', err)
      } finally {
        setLoading(false)
      }
    }
    loadDetails()
  }, [invitation.id])

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[400px] sm:w-[540px]">
        <SheetTitle>Invitation Details</SheetTitle>
        <SheetDescription>
          Manage invitation lifecycle and track status
        </SheetDescription>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-pulse">Loading details...</div>
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            {/* Basic Info */}
            <div>
              <h4 className="font-medium text-primary mb-3">Basic Information</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted">Email:</span>
                  <span className="font-medium">{details.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Role:</span>
                  <span className="font-medium">{details.role_template_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Status:</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(details.status)}`}>
                    {details.status.charAt(0).toUpperCase() + details.status.slice(1)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Created by:</span>
                  <span className="font-medium">{details.creator_name || 'Unknown'}</span>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div>
              <h4 className="font-medium text-primary mb-3">Timeline</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <div className="text-sm">
                    <span className="font-medium">Created</span>
                    <span className="text-muted ml-2">{formatTimeAgo(details.created_at)}</span>
                  </div>
                </div>
                {details.sent_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div className="text-sm">
                      <span className="font-medium">Sent</span>
                      <span className="text-muted ml-2">{formatTimeAgo(details.sent_at)}</span>
                    </div>
                  </div>
                )}
                {details.opened_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <div className="text-sm">
                      <span className="font-medium">Viewed</span>
                      <span className="text-muted ml-2">{formatTimeAgo(details.opened_at)}</span>
                    </div>
                  </div>
                )}
                {details.accepted_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                    <div className="text-sm">
                      <span className="font-medium">Accepted</span>
                      <span className="text-muted ml-2">{formatTimeAgo(details.accepted_at)}</span>
                    </div>
                  </div>
                )}
                {details.revoked_at && (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <div className="text-sm">
                      <span className="font-medium">Revoked</span>
                      <span className="text-muted ml-2">{formatTimeAgo(details.revoked_at)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Security Config */}
            <div>
              <h4 className="font-medium text-primary mb-3">Security Configuration</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted">MFA Required:</span>
                  <span className={details.mfa_required ? 'text-green-600' : 'text-red-600'}>
                    {details.mfa_required ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Expires:</span>
                  <span className="font-medium">{new Date(details.expires_at).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted">Resend Count:</span>
                  <span className="font-medium">{details.resend_count || 0}</span>
                </div>
              </div>
            </div>

            {/* Message */}
            {details.message && (
              <div>
                <h4 className="font-medium text-primary mb-2">Personal Message</h4>
                <p className="text-sm text-muted italic">"{details.message}"</p>
              </div>
            )}

            {/* Actions */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-primary mb-3">Actions</h4>
              <div className="flex gap-2">
                {details.status === 'pending' && (
                  <>
                    <button className="btn secondary text-xs">Resend</button>
                    <button className="btn secondary text-xs">Extend</button>
                  </>
                )}
                {details.status !== 'revoked' && details.status !== 'accepted' && (
                  <button className="btn secondary text-xs text-red-600">Revoke</button>
                )}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

export default TeamInvitations